import * as React from "react";
import { v4 as uuidv4 } from "uuid";
import { formInitialize, formSet, formUninitialize, useCpFormSubmit } from "../gvc/testNewForm/utils";
import { useAppDispatch } from "../../store/hooks";
import { formActions } from "../../store/formSlice";
import { Modal, notification } from "antd";
import { linksOf, request } from "../../services/cpln";
import { CpFormPromptDetail } from "../../components/antd/form/CpFormPromptDetail";
import { CpFormContext } from "../../components/form/CpFormContext";
import { CpFormValidator } from "../gvc/testNewForm/CpFormValidator";
import { CpPreviewModal } from "../workload/draft/previewModal";
import { CpDebugModal } from "../workload/draft/debugModal";
import { Gvc } from "../../schema/types/gvc/entity";
import { useGetGvcQuery } from "../../store/api/gvcApi";
import { gvcToInput, gvcToPatch, inputToGvc } from "./utils";
import { GvcInput } from "../../schema/types/gvc/input";
import { GvcDetailContent } from "./detailContent";
import { getValidator } from "./create/validator";
import { getDirtyChecker } from "./create/dirtyChecker";
import { CpFormDirtyChecker } from "./testNewForm/CpFormDirtyChecker";

interface Props {
  gvc: Gvc;
}
export const GvcDetailForm: React.FC<Props> = ({ gvc }) => {
  const dispatch = useAppDispatch();
  const formIdRef = React.useRef(uuidv4());
  const formId = formIdRef.current;

  const { refetch } = useGetGvcQuery(linksOf(gvc).self!);

  // form init
  const [isInitialized, setIsInitialized] = React.useState<boolean>(false);
  React.useEffect(() => {
    const input = gvcToInput(gvc);
    formInitialize(formId, input);
    setIsInitialized(true);
    return () => {
      formUninitialize(formId);
    };
  }, []);

  // confirm
  React.useEffect(() => {
    dispatch(formActions.setInitialValues({ id: formId, values: gvcToInput(gvc) }));
  }, [gvc.version]);

  // validation/dirty/submit
  const validatorRef = React.useRef(getValidator(formId));
  const validator = validatorRef.current;
  const dirtyCheckerRef = React.useRef(getDirtyChecker(formId));
  const dirtyChecker = dirtyCheckerRef.current;
  const submit = useCpFormSubmit({ id: formId, validationFn: validator, onSubmit: handleSubmit });

  async function handleSubmit(input: GvcInput) {
    try {
      let body = gvcToPatch(inputToGvc(input), { id: input.id, version: input.version });
      const method = "patch";
      const url = linksOf(gvc).self!;

      await request({
        method: method,
        url: url,
        body: body,
      });

      formSet(formId, `meta.applied`, true);

      notification.success({
        message: "Success",
        description: "GVC is updated",
      });

      refetch();
    } catch (e) {
      if (e?.response?.status === 409) {
        Modal.warning({
          title: "Update Conflict",
          content: "This resource has been modified since you opened it. The form will be refreshed with the latest data and your changes will be lost.",
          onOk: () => {
            refetch();
          },
        });
        return;
      }

      let errorMessage = e?.response?.data?.message;
      if (!errorMessage) errorMessage = e.message;
      notification.warning({
        message: "Failed",
        description: errorMessage,
      });
    }
  }

  if (!isInitialized) {
    return null;
  }

  return (
    <CpFormContext.Provider value={{ id: formId, submit: submit }}>
      <GvcDetailContent gvc={gvc} refetch={refetch} />
      <CpFormValidator validationFn={validator} />
      <CpFormDirtyChecker checker={dirtyChecker} />
      <CpFormPromptDetail link={linksOf(gvc).self!} inputToItemFn={inputToGvc} />
      <CpPreviewModal inputToItemFn={inputToGvc} kind="gvc" namePath="name" title="GVC" />
      <CpDebugModal inputToItemFn={inputToGvc} kind="gvc" namePath="name" title="GVC" />
    </CpFormContext.Provider>
  );
};
